/**
 * SYSTEM AUFQ Design Tokens — single source of truth.
 *
 * Everything visual derives from this file:
 *  - `theme.ts` feeds these into Ant Design's ConfigProvider.
 *  - `index.css` mirrors them as CSS custom properties (`--aufq-*`).
 *
 * When you need a colour/spacing/radius/shadow in code, import from here.
 * Hard-coded hex values in feature files are deprecated.
 */

// ─── Colour scale (semantic) ────────────────────────────────────────────
//
// "soft" = tinted background suitable for badges/pills.
// "ink"  = stronger variant for hover/active states.

export const colors = {
  light: {
    // Base surfaces
    bg: '#F5F7FA',
    bgSubtle: '#EEF2F6',
    bgCard: '#FFFFFF',
    bgElevated: '#FFFFFF',

    // Text
    ink: '#1F2A37',
    ink2: '#4B5966',
    ink3: '#8291A0',

    // Lines / borders
    line: '#E3E8EE',
    line2: '#EEF2F6',

    // Brand / accent (deep navy)
    accent: '#1E5A8E',
    accentSoft: '#E3EEF8',
    accentInk: '#174A76',

    // Gold (secondary brand)
    gold: '#8A6E14',
    goldSoft: '#F8F0D8',

    // Semantic status
    success: '#1F7A4C',
    successSoft: '#E6F2EB',
    warn: '#B07A11',
    warnSoft: '#FBF0DC',
    danger: '#B3261E',
    dangerSoft: '#FBE9E7',
    info: '#3C82BC',
    infoSoft: '#E3EEF8',
  },
  dark: {
    bg: '#0E1D26',
    bgSubtle: '#142834',
    bgCard: '#162C38',
    bgElevated: '#1B3543',

    ink: '#F1EFE8',
    ink2: '#B9C3C9',
    ink3: '#7E8A92',

    line: '#24404F',
    line2: '#1E3644',

    accent: '#7FB8D4',
    accentSoft: '#1D3D4E',
    accentInk: '#A6D0E4',

    gold: '#E3C766',
    goldSoft: '#3A3116',

    success: '#45A877',
    successSoft: '#163A2A',
    warn: '#D9A441',
    warnSoft: '#3A3116',
    danger: '#E0736A',
    dangerSoft: '#3D1F1C',
    info: '#7FB8D4',
    infoSoft: '#1D3D4E',
  },
} as const

export type ColorMode = keyof typeof colors // 'light' | 'dark'
export type ColorToken = keyof typeof colors.light

// ─── Radius / shape ─────────────────────────────────────────────────────

export const radius = {
  sm: 8,
  md: 12,
  lg: 16,
} as const

// ─── Spacing (4-based scale) ────────────────────────────────────────────

export const spacing = {
  '0': 0,
  '1': 4,
  '2': 8,
  '3': 12,
  '4': 16,
  '5': 20,
  '6': 24,
  '8': 32,
  '10': 40,
  '12': 48,
  '16': 64,
} as const

// ─── Shadows ────────────────────────────────────────────────────────────

export const shadows = {
  light: {
    xs: '0 1px 0 0 rgba(0, 0, 0, 0.03)',
    sm: '0 1px 2px 0 rgba(0,0,0,0.04), 0 0 0 1px rgba(0,0,0,0.03)',
    md: '0 4px 12px -2px rgba(0,0,0,0.06), 0 2px 4px -2px rgba(0,0,0,0.04)',
    lg: '0 20px 50px -12px rgba(0,0,0,0.15)',
  },
  dark: {
    xs: '0 1px 0 0 rgba(0, 0, 0, 0.3)',
    sm: '0 1px 3px 0 rgba(0,0,0,0.3)',
    md: '0 4px 12px -2px rgba(0,0,0,0.5)',
    lg: '0 20px 50px -12px rgba(0,0,0,0.6)',
  },
} as const

// ─── Typography ─────────────────────────────────────────────────────────

export const typography = {
  /** خط النص — وضوح عالٍ بالأحجام الصغيرة */
  fontFamilyAr: "'Almarai','Segoe UI',sans-serif",
  /** خط العرض — عناوين وأرقام وأزرار، هندسي يوازي اللوكو */
  fontFamilyDisplay: "'Almarai','Segoe UI',sans-serif",
  fontFamilyEn: "'Inter','Segoe UI',sans-serif",
  fontFamilyNum: "'Almarai','Segoe UI',sans-serif",
  fontSize: 14,
  fontSizeHeading1: 22,
  fontSizeHeading2: 18,
  fontSizeHeading3: 16,
  fontSizeHeading4: 15,
} as const

// ─── Motion ─────────────────────────────────────────────────────────────

export const motion = {
  durationFast: '0.12s',
  durationMid: '0.2s',
  durationSlow: '0.4s',
  easeStandard: 'cubic-bezier(0.2, 0.7, 0.2, 1)',
} as const

// ─── «ستوديو» — الثيم الثاني ────────────────────────────────────────────
//
// أزرق حيّ على بياض ناصع ورمادي بارد، زوايا ١٤–٢٠، ظلال ناعمة جداً وتوهّج
// للزر الأساسي — بطاقات تتنفّس بمسافات واسعة. يُختار من «المظهر» بالترويسة،
// ومرآته في CSS هي `html[data-style='studio']` (src/styles/studio.css).

export const studioColors = {
  light: {
    bg: '#F4F6F9',
    bgSubtle: '#F7F8FA',
    bgCard: '#FFFFFF',
    bgElevated: '#FFFFFF',

    ink: '#0F172A',
    ink2: '#475569',
    ink3: '#94A3B8',

    line: '#E9ECF2',
    line2: '#F1F3F7',

    accent: '#2F6BFF',
    accentSoft: '#EEF3FF',
    accentInk: '#1E4FE0',

    gold: '#C98A0B',
    goldSoft: '#FFF5DD',

    success: '#16A34A',
    successSoft: '#E7F7EE',
    warn: '#E08A00',
    warnSoft: '#FFF3E0',
    danger: '#E5484D',
    dangerSoft: '#FDECEC',
    info: '#0E9CE0',
    infoSoft: '#E5F5FD',
  },
  dark: {
    bg: '#0A0E16',
    bgSubtle: '#131A26',
    bgCard: '#10151F',
    bgElevated: '#161D2A',

    ink: '#E8EDF5',
    ink2: '#A6B1C2',
    ink3: '#6B778A',

    line: '#1E2635',
    line2: '#18202D',

    accent: '#5B8CFF',
    accentSoft: '#15234A',
    accentInk: '#93B1FF',

    gold: '#F0BE4A',
    goldSoft: '#33290F',

    success: '#3FCB7A',
    successSoft: '#0F2E1E',
    warn: '#F5A524',
    warnSoft: '#35260B',
    danger: '#FF6B70',
    dangerSoft: '#3A1719',
    info: '#38BDF8',
    infoSoft: '#0C2A3A',
  },
} as const

/** خط «ستوديو» — Nunito للاتيني والأرقام و Rabar للعربي (كنظام Homele) */
export const studioFont = "'Nunito','Rabar','Segoe UI',sans-serif"

export const studioRadius = {
  sm: 10,
  md: 14,
  lg: 18,
  xl: 22,
} as const

export const studioShadows = {
  light: {
    xs: '0 1px 2px rgba(16, 24, 40, 0.04)',
    sm: '0 1px 3px rgba(16, 24, 40, 0.06), 0 1px 2px rgba(16, 24, 40, 0.03)',
    md: '0 12px 32px -12px rgba(16, 24, 40, 0.14)',
    lg: '0 24px 60px -18px rgba(16, 24, 40, 0.22)',
    glow: '0 10px 24px -8px rgba(47, 107, 255, 0.55)',
  },
  dark: {
    xs: '0 1px 2px rgba(0, 0, 0, 0.4)',
    sm: '0 1px 3px rgba(0, 0, 0, 0.45)',
    md: '0 12px 32px -12px rgba(0, 0, 0, 0.6)',
    lg: '0 24px 60px -18px rgba(0, 0, 0, 0.75)',
    glow: '0 10px 26px -8px rgba(91, 140, 255, 0.6)',
  },
} as const

// ─── Bundled export ─────────────────────────────────────────────────────

export const tokens = { colors, radius, spacing, shadows, typography, motion } as const

export type Tokens = typeof tokens

/**
 * Helper: get a colour for the active theme.
 * Useful in JS where reading from CSS vars is awkward (e.g. Recharts series colours).
 *
 *   colorOf('accent', 'light') → '#365181'
 */
export function colorOf(token: ColorToken, mode: ColorMode = 'light'): string {
  return colors[mode][token]
}

/**
 * Mapping of well-known Ant Design / accidental hex codes to their canonical
 * SYSTEM AUFQ CSS variables. Used by the codemod/grep migration in Phase 2.
 *
 * If you see one of these literals in a feature file, replace with the
 * `var(--aufq-*)` listed here.
 */
export const legacyHexMap: Record<string, string> = {
  // Ant Design defaults
  '#1677ff': 'var(--aufq-accent)',
  '#52c41a': 'var(--aufq-success)',
  '#fa8c16': 'var(--aufq-warn)',
  '#cf1322': 'var(--aufq-danger)',
  // Brand canon (already correct, but make them var-driven for theme switching)
  '#365181': 'var(--aufq-accent)',
  '#b08c4a': 'var(--aufq-gold)',
  '#c04f45': 'var(--aufq-danger)',
  '#3e9361': 'var(--aufq-success)',
  '#c89239': 'var(--aufq-warn)',
  '#4e88c5': 'var(--aufq-info)',
}
